/**
 * calc/tradeTypes.ts — THE one home of what a trade row's type means.
 *
 * Every reader of a trade log (the cash kernel, the replays, the edge's
 * validation, the importers, the pages) asks this module rather than spelling
 * the type list out itself, so a new type cannot reach one surface and miss
 * another. No dependency on any other module.
 *
 * The nine types, and what each does to a book:
 *
 *   BUY / SELL   a security trade: shares × price (+/− commission) moves cash
 *                and the position.
 *   DIVIDEND     income from a holding: shares × price (the cash) is credited;
 *                the position is untouched.
 *   SPLIT        a share event: shares holds the ratio; no cash moves.
 *   DEPOSIT      money the owner puts IN (an external flow): +amount.
 *   WITHDRAWAL   money the owner takes OUT (an external flow): −amount.
 *   FEE          a charge the account pays (platform, management, custody,
 *                account, a dealing charge booked apart from its trade, margin
 *                interest): −amount. NOT an external flow — it stays inside the
 *                return, so every performance figure is net of it (GIPS 2020,
 *                net-of-fee return: the return after the investment management
 *                and administrative fees the account paid).
 *   INTEREST     cash interest the account receives: +amount. Income, inside
 *                the return, reported beside dividends.
 *   TAX          tax withheld at source on income (a dividend's or interest's
 *                withholding, "WHT", "foreign tax"): −amount. NOT an external
 *                flow — like a fee it stays inside the return, so returns are
 *                net of withholding (GIPS 2020 for Firms, the provisions on
 *                returns: calculated net of non-recoverable withholding taxes
 *                on dividends, interest and capital gains). Optionally
 *                attributed to the ticker whose income it was taken from, and
 *                to that payment by `incomeDate`; `reclaimable` marks tax a
 *                treaty lets the owner claim back, which is only labelled —
 *                nothing accrues the claim before it is paid.
 *
 * DEPOSIT, WITHDRAWAL, FEE, INTEREST and TAX are AMOUNT rows: their cash is
 * `amount` (positive; the type gives the direction), in `currency` or the
 * book's base, on the `$CASH` ticker or, for a FEE or a TAX, optionally the
 * ticker it was charged on (an attribution only: it never moves that holding).
 *
 * A REFUND of a fee or of withheld tax (a platform-fee rebate, an AMC rebate,
 * a reclaim of withholding the treaty rate did not allow) is the same type
 * with `refund: true`: money back, +amount, still inside the return. It is a
 * negative fee or a negative tax, not a DEPOSIT, because it gives back a cost
 * the return already bore: recorded as a deposit, the cost would stay in the
 * return as a permanent loss while the money back inflated the owner's
 * contributions (GIPS 2020: a net-of-fee return is net of the fees actually
 * borne, so a rebated fee is deducted net of its rebate).
 */

/** Every type a stored trade row may carry. */
export const TRADE_TYPES = [
  'BUY', 'SELL', 'DIVIDEND', 'DEPOSIT', 'WITHDRAWAL', 'SPLIT', 'FEE', 'INTEREST', 'TAX',
] as const;

export type TradeTypeName = (typeof TRADE_TYPES)[number];

export function isTradeType(type: unknown): type is TradeTypeName {
  return typeof type === 'string' && (TRADE_TYPES as readonly string[]).includes(type);
}

/** Rows whose cash is `amount` (direction from the type), not shares × price. */
export const AMOUNT_TYPES = ['DEPOSIT', 'WITHDRAWAL', 'FEE', 'INTEREST', 'TAX'] as const;

export type AmountTypeName = (typeof AMOUNT_TYPES)[number];

export function isAmountRow(type: string): boolean {
  return (AMOUNT_TYPES as readonly string[]).includes(type);
}

/**
 * External flows: the owner's own money crossing the account's boundary.
 * These — and only these — are what a time-weighted return neutralises, what
 * net contributions sum and what a money-weighted return reads as flows. A FEE,
 * INTEREST and a TAX are deliberately NOT here: they are the account's own gain
 * and cost, so the return is net of fees and of withholding and includes
 * interest (GIPS 2020).
 */
export function isExternalFlow(type: string): boolean {
  return type === 'DEPOSIT' || type === 'WITHDRAWAL';
}

/** Income the account receives: reported together where the app reports income. */
export function isIncome(type: string): boolean {
  return type === 'DIVIDEND' || type === 'INTEREST';
}

/** A cost the account bears inside its return, which a refund can give back. */
export function isRefundableCost(type: string): boolean {
  return type === 'FEE' || type === 'TAX';
}

/** +1 for an amount type that adds cash, −1 for one that removes it, 0 otherwise. */
export function amountSign(type: string): 1 | -1 | 0 {
  if (type === 'DEPOSIT' || type === 'INTEREST') return 1;
  if (type === 'WITHDRAWAL' || type === 'FEE' || type === 'TAX') return -1;
  return 0;
}

/** True for a FEE or TAX row that gives the cost back (`refund: true`). */
export function isRefundRow(t: { type: string; refund?: boolean | null }): boolean {
  return t.refund === true && isRefundableCost(t.type);
}

/**
 * The direction of one amount ROW: the type's, flipped for a refund of a fee
 * or of withheld tax. THE sign the cash kernel applies.
 */
export function amountRowSign(t: { type: string; refund?: boolean | null }): 1 | -1 | 0 {
  const s = amountSign(t.type);
  if (!isRefundRow(t)) return s;
  return s === -1 ? 1 : s;
}

/** A plain label for each type, one spelling across the pages and exports. */
export const TRADE_TYPE_LABELS: Record<TradeTypeName, string> = {
  BUY: 'Buy',
  SELL: 'Sell',
  DIVIDEND: 'Dividend',
  DEPOSIT: 'Deposit',
  WITHDRAWAL: 'Withdrawal',
  SPLIT: 'Split',
  FEE: 'Fee',
  INTEREST: 'Interest',
  TAX: 'Tax withheld',
};

/** The label of one row, a refund named as one ("Fee refund", "Tax refund"). */
export function tradeRowLabel(t: { type: string; refund?: boolean | null }): string {
  if (isRefundRow(t)) return t.type === 'FEE' ? 'Fee refund' : 'Tax refund';
  return isTradeType(t.type) ? TRADE_TYPE_LABELS[t.type] : t.type;
}

/**
 * Read a type from free text — a broker's transaction description, a mapped
 * file's type column. THE one classifier: the importers and the column mapper
 * call it, and anything built on them (import templates) should too.
 *
 * Answers null for text it cannot place, so the caller lists the row as an
 * error rather than guessing. The order is the rule:
 *
 *   1. tax relief (a pension's relief at source, paid in by HMRC)
 *                                          → DEPOSIT (money from outside)
 *   2. a tax on a dealing (stamp duty, SDRT, a financial transaction tax,
 *      FTT, the PTM levy)                  → FEE (a cost of the trade)
 *   3. withholding ("WHT", "withheld") and any other tax ("foreign tax",
 *      "US tax", "dividend tax", "interest tax"; tax-free and tax-exempt
 *      excepted)                           → TAX (a refund of it is the same
 *      type; `readsAsRefund` says which)
 *   4. dividend text                       → DIVIDEND
 *   5. a refund or rebate of a fee or charge → FEE (a refund; `readsAsRefund`);
 *      any other refund or rebate          → null (money back is neither a
 *      charge nor income; the caller decides, e.g. a pension's unit rebate)
 *   6. buy / sell verbs                    → BUY / SELL ("sale for charges" is
 *      units sold to PAY a charge: a SELL; its charge is a separate row)
 *   7. interest the account PAYS (margin, debit, overdraft, loan, "interest
 *      charged")                          → FEE; any other interest → INTEREST
 *   8. a fee, charge, commission, AMC, custody, platform or admin fee, levy
 *                                          → FEE (before the cash verbs, so a
 *      "withdrawal fee" is the fee, not a withdrawal)
 *   9. deposit / top up / contribution     → DEPOSIT; withdraw → WITHDRAWAL
 */
export function classifyTradeTypeText(text: unknown): TradeTypeName | null {
  const t = String(text ?? '').toLowerCase().trim();
  if (!t) return null;
  if (/tax relief/.test(t)) return 'DEPOSIT';
  if (/stamp duty|\bsdrt\b|transaction tax|\bftt\b|\bptm levy\b/.test(t)) return 'FEE';
  if (/\bwht\b|withh?old|withheld/.test(t)) return 'TAX';
  if (/\btax(es)?\b/.test(t) && !/tax.?(free|exempt)/.test(t)) return 'TAX';
  if (/div/.test(t)) return 'DIVIDEND';
  if (/\b(rebate|refund)/.test(t)) {
    return /\bfees?\b|\bcharges?\b|commission|\bamc\b|management|custody|platform|\badmin\b/.test(t) ? 'FEE' : null;
  }
  if (/buy|bought|purchase|acquisition/.test(t)) return 'BUY';
  if (/sell|sold|\bsale\b|disposal/.test(t)) return 'SELL';
  if (/interest/.test(t)) {
    return /margin|debit|overdraft|loan|borrow|interest (charged|paid|expense)/.test(t) ? 'FEE' : 'INTEREST';
  }
  if (/\bfees?\b|\bcharges?\b|commission|\bamc\b|custody|platform|\badmin\b|levy/.test(t)) return 'FEE';
  if (/deposit|top.?up|contribution/.test(t)) return 'DEPOSIT';
  if (/withdraw/.test(t)) return 'WITHDRAWAL';
  return null;
}

/**
 * True when the text says money is coming BACK (a rebate, a refund, a reclaim,
 * a reversal, a tax credit). The importers read it beside the classifier: a
 * FEE or TAX row whose text reads as a refund is stored with `refund: true`.
 * An export that signs its cash says so better, and where it does the sign
 * wins (money in on a fee or tax row is a refund).
 */
export function readsAsRefund(text: unknown): boolean {
  const t = String(text ?? '').toLowerCase();
  // "Fees and rebates" names a category holding both: the text says neither.
  if (/fees? (and|&) rebates?/.test(t)) return false;
  return /\b(rebate|refund|reclaim|reversal|reversed|repaid|returned)|tax credit/.test(t);
}

/**
 * An INTEREST row written with a negative amount is interest the account paid:
 * a FEE of that magnitude. Every other type keeps its own direction (a FEE's
 * sign is how the export writes a debit, so its magnitude is the fee).
 */
export function typeForSignedAmount(type: TradeTypeName, signedAmount: number): TradeTypeName {
  if (type === 'INTEREST' && signedAmount < 0) return 'FEE';
  return type;
}
